import { AlertTriangle, Check, Copy, Loader2, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { checkSidecars, type SidecarCheckResponse } from '../../api';
import { assetTypeLabel, type AssetType } from '../../constants/assetTypes';
import { AssetDropzone } from './AssetDropzone';
import { describeError } from './batchUpload';
import { MAX_LABEL_LENGTH } from './constants';
import type { PickedFile } from './olaDetect';
import {
  pairSidecars,
  readSidecars,
  toSidecarRequests,
  type DroppedFile,
  type Pairing,
  type SidecarFile,
} from './pairSidecars';
import { useSidecarUpload } from './useUploadRun';

/**
 * Upload with JSON: drop source images and master images, each with a JSON
 * file of the same name, and each image is uploaded with the metadata its
 * JSON holds. A master's JSON names its source images and carries only its
 * format and copy; the rest is inherited from them on the server.
 *
 * Separate from `UploadBatchModal` on purpose. That flow is one metadata form
 * for the whole batch; here there is no form at all. The JSON is the
 * download's sidecar format, and what it may say is the backend's to judge
 * (`src/core/sidecar.py`), so this component only pairs files and asks.
 *
 * A file that cannot be uploaded is rejected and listed with the reason. Up to
 * `max_rejected` rejected files, the uploader is warned and may skip them to
 * upload the rest. Past it, the whole drop is refused: that many is more
 * likely the wrong folder than a few typos.
 */

type Phase = 'select' | 'uploading';

/** Fallback until the first check answers; the backend's `max_rejected` is the authority. */
const DEFAULT_MAX_REJECTED = 10;

/** What each kind's JSON looks like, shown on request. Labels as the app shows them. */
const EXAMPLE_SIDECAR = `{
  "nameplate": ["Puma"],
  "carline_code": ["PUM"],
  "business_unit": ["Ford Blue"],
  "tier": ["Tier 1"],
  "domain": "Adventure",
  "channel_rights": ["Digital", "Print"],
  "country_rights": ["Germany", "UK"],
  "expiry_rights": "2027-06-30",
  "source_agency": "Wieden+Kennedy",
  "ai_indicator": "No AI",
  "is_lifestyle_image": false
}`;

const EXAMPLE_MASTER_SIDECAR = `{
  "asset_type": "Master image",
  "source_image_codes": ["SI-000042"],
  "format_type": "Performance Max",
  "format_size": "1.91x1",
  "copy_elements": [
    { "type": "Headline", "value": "The all-electric Puma Gen-E" },
    { "type": "CTA", "value": "Book a test drive" }
  ]
}`;

interface SidecarUploadModalProps {
  onClose: () => void;
  /** Called once uploads finish, so the caller can invalidate the asset list. */
  onUploaded: () => void;
}

/** The selection as paired, and the backend's answer once it has one. */
interface CheckState {
  dropped: DroppedFile[];
  pairing: Pairing;
  result: SidecarCheckResponse | null;
}

interface Verdict {
  image: SidecarFile;
  /** Null when accepted. */
  reason: string | null;
  /** What it will be uploaded as, when accepted. */
  assetType: AssetType | null;
}

/**
 * Pair each image with the backend's verdict.
 *
 * The response splits files into two lists but keeps request order within
 * each, so walking the images against `accepted` in step recovers which is
 * which.
 */
function pairVerdicts(images: SidecarFile[], check: SidecarCheckResponse): Verdict[] {
  let acceptedIndex = 0;
  let rejectedIndex = 0;
  return images.map((image) => {
    const accepted = check.accepted[acceptedIndex];
    if (accepted && accepted.filename === image.file.name) {
      acceptedIndex += 1;
      return { image, reason: null, assetType: accepted.asset_type };
    }
    const rejected = check.rejected[rejectedIndex];
    rejectedIndex += 1;
    return { image, reason: rejected?.reason ?? 'not accepted', assetType: null };
  });
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export function SidecarUploadModal({ onClose, onUploaded }: SidecarUploadModalProps) {
  const [phase, setPhase] = useState<Phase>('select');
  const [dropped, setDropped] = useState<DroppedFile[]>([]);
  const [label, setLabel] = useState('');
  const [check, setCheck] = useState<CheckState | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const upload = useSidecarUpload();
  const nextKey = useRef(0);

  // Re-paired and re-checked on every change to the selection. A stale answer
  // is discarded by identity: it is for the selection it was asked about, and
  // showing it against a newer one would pair verdicts with the wrong files.
  useEffect(() => {
    if (dropped.length === 0) {
      setCheck(null);
      setCheckError(null);
      return;
    }
    const controller = new AbortController();
    setCheckError(null);
    const run = async () => {
      const parsed = await readSidecars(dropped.map(({ file }) => file));
      if (controller.signal.aborted) return;
      const pairing = pairSidecars(dropped, parsed);
      // Shown straight away: what the browser rejected needs no round trip.
      setCheck({ dropped, pairing, result: null });
      if (pairing.images.length === 0) return;
      const result = await checkSidecars(toSidecarRequests(pairing.images), controller.signal);
      if (controller.signal.aborted) return;
      setCheck({ dropped, pairing, result });
    };
    run().catch((error: unknown) => {
      if (controller.signal.aborted) return;
      setCheckError(describeError(error, 'Could not check the files'));
    });
    return () => controller.abort();
  }, [dropped]);

  const current = check && check.dropped === dropped ? check : null;
  const isChecking =
    dropped.length > 0 &&
    checkError === null &&
    (current === null || (current.pairing.images.length > 0 && current.result === null));
  const verdicts = useMemo(
    () => (current?.result ? pairVerdicts(current.pairing.images, current.result) : []),
    [current],
  );
  const ready = verdicts.filter((verdict) => verdict.reason === null);
  const rejectedRows = [
    ...(current?.pairing.rejected ?? []),
    ...verdicts
      .filter((verdict) => verdict.reason !== null)
      .map((verdict) => ({
        key: verdict.image.key,
        filename: verdict.image.file.name,
        reason: verdict.reason as string,
        jsonKey: verdict.image.jsonKey,
        overLimit: false,
      })),
  ];
  const maxRejected = current?.result?.max_rejected ?? DEFAULT_MAX_REJECTED;
  // Files past the cap are listed but not counted: they say nothing about
  // whether the drop is described correctly.
  const rejectedAll = rejectedRows.filter((row) => !row.overLimit).length > maxRejected;
  // Up to the limit, the rest can still go, but only as a choice: the button
  // says it skips the rejected files rather than just "Upload".
  const skipping = !rejectedAll && rejectedRows.length > 0 && ready.length > 0;

  // A file named like one already in the list replaces it: dropping a
  // corrected JSON again is how a rejection gets fixed, and keeping both would
  // reject them as a name clash. Within one drop, a clash is still a clash.
  const handleFiles = useCallback((picked: PickedFile[]) => {
    if (picked.length === 0) return;
    const names = new Set(picked.map(({ file }) => file.name));
    setDropped((existing) => [
      ...existing.filter((entry) => !names.has(entry.file.name)),
      ...picked.map(({ file }) => ({ key: `f${nextKey.current++}`, file })),
    ]);
  }, []);

  const removeFiles = useCallback((...keys: string[]) => {
    setDropped((existing) => existing.filter((entry) => !keys.includes(entry.key)));
  }, []);

  const clearAll = useCallback(() => setDropped([]), []);

  const handleStart = useCallback(async () => {
    setPhase('uploading');
    const result = await upload.start(
      ready.map((verdict) => verdict.image),
      label,
    );
    // Fired even on a partial run: whatever confirmed is staged and should
    // appear behind the modal.
    if (result.uploaded > 0) onUploaded();
  }, [label, onUploaded, ready, upload]);

  const started = phase === 'uploading';
  const isFinished = started && !upload.isUploading;
  const summary = useMemo(() => {
    const done = upload.items.filter((item) => item.status === 'done').length;
    const failed = upload.items.filter((item) => item.status === 'failed').length;
    const duplicates = upload.items.filter((item) => item.duplicateOf?.length).length;
    return { done, failed, duplicates };
  }, [upload.items]);

  const handleClose = useCallback(() => {
    if (upload.isUploading) upload.cancel();
    onClose();
  }, [upload, onClose]);

  const canUpload = !isChecking && checkError === null && !rejectedAll && ready.length > 0;

  return (
    <>
      <div className="fixed inset-0 z-[90] bg-black/50" />
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        onClick={(event) => {
          if (event.target === event.currentTarget && !upload.isUploading) handleClose();
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => event.preventDefault()}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sidecar-upload-title"
          className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
            <h2 id="sidecar-upload-title" className="text-[14px] font-bold text-slate-800">
              {isFinished ? 'Upload complete' : started ? 'Uploading…' : 'Upload with JSON'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="ml-auto rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {!started && (
              <>
                <p className="text-[11.5px] text-slate-500">
                  Drop source images or master images, each with a JSON file of the same name (
                  <span className="font-mono">puma-beach.png</span> +{' '}
                  <span className="font-mono">puma-beach.json</span>). Images without a JSON, or
                  whose JSON the library can’t read, are not uploaded.
                </p>
                <details className="text-[11.5px] text-slate-500">
                  <summary className="cursor-pointer font-medium text-slate-600">
                    What goes in the JSON?
                  </summary>
                  <p className="mt-1.5">
                    <span className="font-medium text-slate-600">A source image:</span> the same
                    format as the download, with all of these fields, values as the app shows them.
                  </p>
                  <pre className="mt-1.5 overflow-x-auto rounded-md bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
                    {EXAMPLE_SIDECAR}
                  </pre>
                  <p className="mt-2">
                    <span className="font-medium text-slate-600">A master image:</span> the
                    published source images it was cut from, its format and its copy. Everything
                    else comes from the source images, so leave it out — they must agree on domain
                    and share at least one tier.
                  </p>
                  <pre className="mt-1.5 overflow-x-auto rounded-md bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
                    {EXAMPLE_MASTER_SIDECAR}
                  </pre>
                  <p className="mt-1.5">An image whose JSON leaves a field out is not uploaded.</p>
                </details>
                <AssetDropzone onFiles={handleFiles} mediaKind="image" withJson />
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                    Batch label <span className="normal-case text-slate-400">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={label}
                    maxLength={MAX_LABEL_LENGTH}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="e.g. Puma shoot — October"
                    className="rounded-md border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-800 outline-none focus:border-[#0014cc]"
                  />
                </label>

                {checkError && (
                  <p role="alert" className="text-[11.5px] text-red-600">
                    {checkError}
                  </p>
                )}

                {rejectedAll && (
                  <div
                    role="alert"
                    className="rounded-md border border-red-200 bg-red-50/60 p-2.5 text-[11.5px] text-red-800"
                  >
                    <p className="flex items-center gap-1.5 font-medium">
                      <XCircle className="h-3.5 w-3.5" strokeWidth={2} />
                      Nothing will be uploaded
                    </p>
                    <p className="mt-1 text-[11px] text-red-700">
                      More than {maxRejected} files were rejected. Fix the files and drop them
                      again.
                    </p>
                  </div>
                )}

                {skipping && !isChecking && (
                  <div
                    role="status"
                    className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11.5px] text-amber-900"
                  >
                    <p className="flex items-center gap-1.5 font-medium">
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                      {plural(rejectedRows.length, 'file')} can’t be uploaded
                    </p>
                    <p className="mt-1 text-[11px] text-amber-800">
                      Fix {rejectedRows.length === 1 ? 'it' : 'them'} first, or skip{' '}
                      {rejectedRows.length === 1 ? 'it' : 'them'} and upload the other{' '}
                      {plural(ready.length, 'image')} now.
                    </p>
                  </div>
                )}

                {rejectedRows.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-medium text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
                      {plural(rejectedRows.length, 'file')} rejected, not uploaded
                    </p>
                    <ul className="mt-1.5 space-y-0.5" aria-label="Rejected files">
                      {rejectedRows.map((row) => (
                        <li
                          key={row.key}
                          className="flex items-start gap-1 text-[11px] text-amber-700"
                        >
                          <span className="min-w-0 flex-1 break-all">
                            <span className="font-medium">{row.filename}</span>
                            {/* The server sends one line per problem, so a file with
                                several is listed rather than run together. */}
                            {row.reason.includes('\n') ? (
                              <ul className="list-disc space-y-0.5 pl-4">
                                {row.reason.split('\n').map((reason) => (
                                  <li key={reason}>{reason}</li>
                                ))}
                              </ul>
                            ) : (
                              <> — {row.reason}</>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeFiles(row.key, ...(row.jsonKey ? [row.jsonKey] : []))
                            }
                            aria-label={`Remove ${row.filename}`}
                            className="rounded p-0.5 text-amber-500 hover:bg-amber-100"
                          >
                            <X className="h-3 w-3" strokeWidth={2} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {ready.length > 0 && (
                  <ul
                    className="divide-y divide-slate-100 rounded-md border border-slate-200"
                    aria-label="Files ready to upload"
                  >
                    {ready.map(({ image, assetType }) => (
                      <li key={image.key} className="flex items-center gap-2 px-2.5 py-1.5">
                        <Check className="h-3.5 w-3.5 shrink-0 text-green-600" strokeWidth={2} />
                        <span
                          className="truncate text-[12px] text-slate-700"
                          title={image.file.name}
                        >
                          {image.file.name}
                        </span>
                        <span className="shrink-0 text-[11px] text-slate-400">
                          + JSON · {assetTypeLabel(assetType)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFiles(image.key, image.jsonKey)}
                          aria-label={`Remove ${image.file.name}`}
                          className="ml-auto rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {started && (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {upload.items.map((item, index) => (
                  <li key={index} className="flex items-center gap-2 px-2.5 py-1.5">
                    <span className="w-4 shrink-0">
                      {item.status === 'uploading' && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#0014cc]" />
                      )}
                      {item.status === 'done' && <Check className="h-3.5 w-3.5 text-green-600" />}
                      {item.status === 'failed' && <X className="h-3.5 w-3.5 text-red-600" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] text-slate-700" title={item.filename}>
                        {item.filename}
                      </p>
                      {item.status === 'uploading' && (
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full bg-[#0014cc] transition-all duration-200"
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      )}
                      {item.error && <p className="text-[11px] text-red-600">{item.error}</p>}
                      {item.duplicateOf?.length ? (
                        <p className="flex items-center gap-1 text-[11px] text-slate-400">
                          <Copy className="h-3 w-3" strokeWidth={2} />
                          Already in the library
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
            <p className="text-[12px] text-slate-500">
              {isFinished
                ? `${summary.done} uploaded${summary.failed > 0 ? `, ${summary.failed} failed` : ''}${
                    summary.duplicates > 0 ? `, ${summary.duplicates} already in the library` : ''
                  }`
                : started
                  ? `Uploading ${plural(upload.items.length, 'image')}`
                  : isChecking
                    ? 'Checking files…'
                    : ready.length > 0
                      ? `${plural(ready.length, 'image')} ready`
                      : 'No images ready'}
            </p>

            {upload.isUploading ? (
              <button
                type="button"
                onClick={upload.cancel}
                className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-red-600 hover:bg-red-50"
              >
                Cancel
              </button>
            ) : isFinished ? (
              <button
                type="button"
                onClick={onClose}
                className="ml-auto rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
              >
                Done
              </button>
            ) : (
              <>
                {dropped.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="ml-auto rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-100"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  disabled={!canUpload}
                  onClick={() => void handleStart()}
                  className={`rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${
                    dropped.length > 0 ? '' : 'ml-auto'
                  }`}
                >
                  {isChecking && <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />}
                  {skipping && !isChecking
                    ? `Skip ${rejectedRows.length} and upload ${plural(ready.length, 'image')}`
                    : `Upload ${ready.length > 0 && !rejectedAll ? `${ready.length} ` : ''}image${
                        ready.length === 1 ? '' : 's'
                      }`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
